import React from "react";
import { BASE_URL } from "../constant/constant";

// Utility function to get nested properties
const getNestedProperty = (obj, path) => {
  return path.split(".").reduce((acc, part) => acc && acc[part], obj);
};

/**
 * A dynamic table component that renders data based on provided columns and custom columns.
 *
 * @param {Array} columns - Array of objects representing standard table columns.
 *                          Each object should have a 'key' for unique identification and a 'header' for column header display.
 * @param {Array} data - Array of objects representing data rows to be displayed in the table.
 *                       Each object should have properties corresponding to the 'key' values in columns.
 * @param {Array} customColumns - Optional. Array of custom column configurations.
 *                                Each object should have a 'header' for column header display and a 'render' function
 *                                that accepts each row item and returns the custom cell content.
 * @param {string} noDataMessage - Message to display when no data is available to show in the table.
 * @param {Object} columnStyles - Optional. Object defining styles for each column based on its 'key'.
 *                                Keys should match 'key' values in 'columns' and 'customColumns'.
 *
 * Example usage:
 * <BuilderTable
 *   columns={[
 *     { key: "name", header: "Name" },
 *     { key: "email", header: "Email" }
 *   ]}
 *   data={[
 *     { id: 1, name: "John Doe", email: "john.doe@example.com" },
 *     { id: 2, name: "Jane Smith", email: "jane.smith@example.com" }
 *   ]}
 *   customColumns={[
 *     { header: "Actions", render: (item) => <ActionButton item={item} /> }
 *   ]}
 *   noDataMessage="No users found."
 *   columnStyles={{
 *     name: { textAlign: "left", fontWeight: "bold" },
 *     email: { textAlign: "center", fontStyle: "italic" },
 *     Actions: { textAlign: "center" }
 *   }}
 * />
 */

const BuilderTable = ({ columns, data, customColumns, noDataMessage, columnStyles = {}, style = {} }) => {
  return (
    <div style={{ margin: "auto", overflowX: "auto" }} className="user_table">
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          tableLayout: "fixed",
          ...style
          // minWidth: "600px"
        }}
      >
        <thead>
          <tr>
            {columns?.map((column) => (
              <th key={column.key} style={columnStyles[column.key]}>
                {column.header}
              </th>
            ))}
            {customColumns &&
              customColumns.map((column) => (
                <th key={column.header} style={columnStyles[column.header]}>
                  {column.header}
                </th>
              ))}
          </tr>
        </thead>
        <tbody>
          {data?.length > 0 ? (
            data.map((item, index) => (
              <tr key={item.id || item.key || item.name}>
                {columns &&
                  columns.map((column) => (
                    <td key={`${item.id}-${column.key}`} style={columnStyles[column.header]}>
                      {column.type === "img" ? (
                        <img
                          src={`${BASE_URL}/api/v1/public/${getNestedProperty(item, column.key)}`}
                          alt={column.header}
                          style={{ objectFit: "cover", height: "100px", width: "auto", maxWidth: "300px" }}
                        />
                      ) : (
                        getNestedProperty(item, column.key)
                      )}
                    </td>
                  ))}
                {customColumns &&
                  customColumns.map((column) => (
                    <td
                      key={`${item.id || item.key || item.name}-${column.header}`}
                      style={columnStyles[column.header]}
                    >
                      {column.render(item, index)}
                    </td>
                  ))}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={`${columns?.length + (customColumns ? customColumns.length : 0).toString()}`}
                className="text_center"
              >
                {noDataMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default BuilderTable;
